/**
 * Persistent cache for downloaded PDF buffers, backed by IndexedDB.
 *
 * Files are downloaded once (see `usePdfDocument`) and stored here so the next
 * open reads straight from disk instead of hitting the network again. Buffers
 * can be large, so a normal object store — not `localStorage` — is used.
 *
 * Entries are keyed by the file's URL *path* (query string stripped). The
 * download URLs are signed, so their query params change on every request; the
 * path stays stable and uniquely identifies the file.
 */

const DB_NAME = "pdf-cache";
const STORE_NAME = "files";
const DB_VERSION = 1;

type CachedFile = {
  buffer: ArrayBuffer;
  storedAt: number;
};

/** Drop entries older than this so the cache can't grow without bound. */
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

/** Stable cache key: the URL without its (signed, ever-changing) query string. */
export function cacheKeyFromUrl(url: string): string {
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.origin + parsed.pathname;
  } catch {
    // Not a parseable URL — fall back to the raw string minus any query.
    return url.split("?")[0];
  }
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

  return dbPromise;
}

/** Read a cached PDF buffer, or `null` if it isn't stored (or is too old). */
export async function getCachedPdf(key: string): Promise<ArrayBuffer | null> {
  try {
    const db = await openDb();
    const entry = await new Promise<CachedFile | undefined>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const request = tx.objectStore(STORE_NAME).get(key);
      request.onsuccess = () => resolve(request.result as CachedFile | undefined);
      request.onerror = () => reject(request.error);
    });

    if (!entry) return null;
    if (Date.now() - entry.storedAt > MAX_AGE_MS) {
      void deleteCachedPdf(key);
      return null;
    }
    return entry.buffer;
  } catch {
    // IndexedDB unavailable (private mode, quota, etc.) — behave as a cache miss.
    return null;
  }
}

/** Persist a downloaded PDF buffer. Failures are swallowed (cache is best-effort). */
export async function putCachedPdf(key: string, buffer: ArrayBuffer): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const entry: CachedFile = { buffer, storedAt: Date.now() };
      tx.objectStore(STORE_NAME).put(entry, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // Out of quota or storage blocked — not fatal, just skip caching.
  }
}

/** Remove a single cached entry. */
export async function deleteCachedPdf(key: string): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      tx.objectStore(STORE_NAME).delete(key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // Best-effort.
  }
}
